import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import '../styles/MultiLoginModel.scss';
import CryptoJS from 'crypto-js';

import { ENV_CONFIG } from 'shared/config/env.config';
import {
  useLazyGetDesignationListQuery,
  useLazyGetAllDistrictListQuery,
  useLazyGetBlockByDistrictQuery,
  useLazyGetUserIdListQuery,
  usePostLoginLogMutation,
  useMultiUserLoginMutation,
  useSsoLoginMutation,
  DesignationItem,
} from './multiLoginApi.service';

interface DropdownOption {
  value: string | number;
  label: string;
}

interface SelectedDesig {
  value: number;
  userType: string;
  userType1: string;
  geoType: 'state' | 'district' | 'block' | 'cluster' | 'school';
}

export interface MultiLoginModalProps {
  visible: boolean;
  onHide: () => void;
  userName: string;
  switchDesignation: string;
}

const DEO_TYPES = new Set(['36', '37', '38']);

const MandatoryLabel = ({ children }: { children: React.ReactNode }) => (
  <b className="inMandatory">{children}</b>
);

export const MultiLoginModal: React.FC<MultiLoginModalProps> = ({
  visible,
  onHide,
  userName,
  switchDesignation,
}) => {
  const [designation, setDesignation] = useState<number | null>(null);
  const [district, setDistrict] = useState<string | number | null>(null);
  const [block, setBlock] = useState<string | number | null>(null);
  const [user, setUser] = useState<string | null>(null);
  const [udiseCode, setUdiseCode] = useState('');
  const [selectedDesig, setSelectedDesig] = useState<SelectedDesig | null>(
    null
  );

  const [designationList, setDesignationList] = useState<DropdownOption[]>([]);
  const [districtList, setDistrictList] = useState<DropdownOption[]>([]);
  const [blockList, setBlockList] = useState<DropdownOption[]>([]);
  const [multiUserList, setMultiUserList] = useState<DropdownOption[]>([]);
  const [designationMap, setDesignationMap] = useState<
    Record<number, DesignationItem>
  >({});
  const [blocksLoaded, setBlocksLoaded] = useState(false);

  // ── RTK Query hooks ─────────────────────────────────────────────────────────
  const [triggerGetDesignations] = useLazyGetDesignationListQuery();
  const [triggerGetDistricts] = useLazyGetAllDistrictListQuery();
  const [triggerGetBlocks] = useLazyGetBlockByDistrictQuery();
  const [triggerGetUserId] = useLazyGetUserIdListQuery();
  const [postLoginLog] = usePostLoginLogMutation();
  const [multiUserLogin] = useMultiUserLoginMutation();
  const [ssoLogin] = useSsoLoginMutation();

  // Angular: splitUsertype()
  const usrTpeAry = useMemo(() => {
    return (switchDesignation || '').split(',').map((pair) => {
      const [userType, userType1] = pair.split('-');
      return { userType, userType1 };
    });
  }, [switchDesignation]);

  // Angular: clickSwapLogin() — reset + fetch on every open
  useEffect(() => {
    if (!visible) return;
    setDesignation(null);
    setDistrict(null);
    setBlock(null);
    setUser(null);
    setUdiseCode('');
    setSelectedDesig(null);
    setDesignationList([]);
    setBlockList([]);
    setMultiUserList([]);
    setBlocksLoaded(false);
    loadDesignations();
    loadDistricts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Angular: getAllDesignation() — GET djangotnemis/getAllUsrCatgry ✅
  const loadDesignations = async () => {
    try {
      const res = await triggerGetDesignations().unwrap();
      if (res.dataStatus) {
        const addedDeoTypes = new Set<string>();
        const list: DropdownOption[] = [];
        const map: Record<number, DesignationItem> = {};
        res.result.forEach((mp, ind) => {
          const isMatch = usrTpeAry.some(
            (u) => u.userType === mp.IndexId && u.userType1 === mp.userType1
          );

          // console.log('switchDesignation prop:', switchDesignation);
          // console.log('parsed usrTpeAry:', usrTpeAry);
          // console.log('API result:', res.result);
          // console.log('filtered list:', list);
          if (!isMatch) return;
          if (DEO_TYPES.has(String(mp.IndexId))) {
            if (addedDeoTypes.has(String(mp.IndexId))) return;
            addedDeoTypes.add(String(mp.IndexId));
          }
          list.push({ value: ind, label: mp.userType });
          map[ind] = mp;
        });
        setDesignationList(list);
        setDesignationMap(map);
      } else {
        setDesignationList([]);
      }
    } catch {
      toast.error('Unable to load designations');
      setDesignationList([]);
    }
  };

  // Angular: getAllDistrict() — GET djangotnemis/alldistrictlist ✅
  const loadDistricts = async () => {
    try {
      const res = await triggerGetDistricts().unwrap();
      if (res.dataStatus) {
        const distArr: any[] =
          (res.result as any)?.schooldist ??
          (Array.isArray(res.result) ? (res.result as any) : []);
        setDistrictList(
          distArr.map((mp: any) => ({ value: mp.id, label: mp.district_name }))
        );
      } else {
        setDistrictList([]);
      }
    } catch {
      toast.error('Unable to load districts');
      setDistrictList([]);
    }
  };

  // Angular: getBlockByDis(dis) — GET php3/api/allblocknameU?districtId= ✅
  // blocksLoaded flag prevents User ID from flashing before API responds
  const loadBlocks = async (dis: string | number, desig: SelectedDesig) => {
    setBlocksLoaded(false);
    try {
      const res = await triggerGetBlocks(dis).unwrap();
      if (res.dataStatus && res.result.length > 0) {
        // blocks exist → show Block dropdown, wait for selection
        setBlockList(
          res.result.map((mp) => ({ value: mp.BlkId, label: mp.BlkName }))
        );
        setBlocksLoaded(true);
      } else {
        // no blocks → skip Block dropdown, load users with district only
        setBlockList([]);
        setBlocksLoaded(true);
        loadUserIds(desig, dis, null);
      }
    } catch {
      setBlockList([]);
      setBlocksLoaded(true);
      loadUserIds(desig, dis, null);
    }
  };

  // Angular: getUserId() — GET php3/api/getUsrIdByDes ✅
  const loadUserIds = async (
    geoOverride?: SelectedDesig,
    disOverride?: string | number | null,
    blkOverride?: string | number | null
  ) => {
    const desig = geoOverride ?? selectedDesig;
    if (!desig) return;
    try {
      const res = await triggerGetUserId({
        usrType: desig.userType,
        usrType1: desig.userType1,
        dis: disOverride !== undefined ? disOverride : district,
        blk: blkOverride !== undefined ? blkOverride : block,
      }).unwrap();
      if (res.dataStatus) {
        setMultiUserList(
          res.result.map((mp) => ({
            value: mp.teacher_id,
            label: `${mp.teacher_id} (${mp.teacherName})`,
          }))
        );
      } else {
        setMultiUserList([]);
      }
    } catch {
      toast.error('Unable to load user list');
      setMultiUserList([]);
    }
  };

  // Angular: handleDesignation(eve)
  const handleDesignationChange = (value: number) => {
    setDesignation(value);
    setDistrict(null);
    setBlock(null);
    setUser(null);
    setUdiseCode('');
    setBlockList([]);
    setMultiUserList([]);
    setBlocksLoaded(false);
    const mp = designationMap[value];
    if (!mp) {
      setSelectedDesig(null);
      return;
    }
    const desig: SelectedDesig = {
      value,
      userType: mp.IndexId,
      userType1: mp.userType1,
      geoType: mp.geoType,
    };
    setSelectedDesig(desig);
    if (desig.geoType === 'state' || desig.geoType === 'cluster') {
      loadUserIds(desig, null, null);
    }
  };

  // Angular: onDistrictChange(districtId)
  const handleDistrictChange = (districtId: string | number) => {
    setDistrict(districtId);
    setBlock(null);
    setUser(null);
    setBlockList([]);
    setMultiUserList([]);
    setBlocksLoaded(false);

    if (selectedDesig?.geoType === 'district') {
      // district → no blocks needed, load users directly
      setBlocksLoaded(true);
      loadUserIds(selectedDesig, districtId, null);
    } else if (selectedDesig?.geoType === 'block') {
      // block → check if this district has blocks
      loadBlocks(districtId, selectedDesig);
    }
  };

  // Angular: block onChange → getUserId()
  const handleBlockChange = (blk: string | number) => {
    setBlock(blk);
    setUser(null);
    setMultiUserList([]);
    loadUserIds(selectedDesig ?? undefined, district, blk);
  };

  // Angular: handleSSOLogin() / apiResponse() — used for school geoType only
  const handleSsoLogin = async (loginUserName: string) => {
    try {
      const res = await ssoLogin({
        records: { emis_username: loginUserName, type: 1 },
      }).unwrap();
      if (res?.records) {
        const encryptedData = encryptSsoUsername(loginUserName);
        const link = `${ENV_CONFIG.siteBaseURL}?userLogin=${encryptedData}`;
        window.open(link, '_blank');
        console.log('siteBaseURL:', ENV_CONFIG.siteBaseURL);
      } else {
        toast.error((res as any)?.message || 'SSO login failed');
      }
    } catch {
      toast.error('SSO login failed');
    }
    // debugger;
  };

  const handleMultiUserLogin = async (mode: 1 | 2, loginUserName: string) => {
    try {
      const res = await multiUserLogin({ username: loginUserName }).unwrap();

      if (!res?.dataStatus) {
        toast.error(res?.message || 'Login failed');
        return;
      }

      const newToken = res?.records?.token;
      if (!newToken) {
        toast.error('Token not found');
        return;
      }

      if (mode === 1) {
        localStorage.setItem('backUser', userName);
      } else {
        localStorage.removeItem('backUser');
      }

      localStorage.setItem('token', newToken);
      localStorage.setItem('user_token', newToken);

      // If currently on mail-request page, navigate to dashboard before reload
      if (window.location.pathname === '/mail-request') {
        window.location.href = '/dashboard';
      } else {
        window.location.reload();
      }
    } catch {
      toast.error('Login failed');
    }
  };
  // Angular: postLoginLog() — POST php3/api/postLoginLog ✅
  // On success → SSO (school) or multiUserLogin (all others)
  const handlePostLoginLog = async (loginUserName: string) => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const time = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    try {
      const res = await postLoginLog({
        records: {
          IndexId: '',
          userId: userName,
          device: '',
          deviceDetails: navigator.userAgent,
          ipAddress: '',
          type: 'in',
          time,
          toUserId: loginUserName,
        },
      }).unwrap();
      if (res?.dataStatus) {
        if (selectedDesig?.geoType === 'school') handleSsoLogin(loginUserName);
        else handleMultiUserLogin(1, loginUserName);
      }
    } catch {
      toast.error('Unable to record login');
    }
  };

  // Angular: submitMultiLogin(1)
  const handleSubmit = () => {
    const geo = selectedDesig?.geoType;
    const blockRequired =
      geo === 'block' && blocksLoaded && blockList.length > 0;

    const isValid =
      (geo === 'state' && !!user) ||
      (geo === 'cluster' && !!user) ||
      (geo === 'district' && !!district && !!user) ||
      (geo === 'block' &&
        !!district &&
        (!blockRequired || !!block) &&
        !!user) ||
      (geo === 'school' &&
        !!udiseCode &&
        (udiseCode.length === 11 || udiseCode.length === 8));

    if (!isValid) {
      if (geo === 'school' && udiseCode.length !== 11 && udiseCode.length !== 8)
        toast.error('Invalid udise code / emis id');
      else toast.error('Please fill all fields');
      return;
    }
    if (geo !== 'school') {
      handlePostLoginLog(user as string);
      return;
    }
    if (selectedDesig?.userType === '1' && udiseCode.length === 11)
      handlePostLoginLog(udiseCode);
    else if (selectedDesig?.userType !== '1' && udiseCode.length === 8)
      handlePostLoginLog(udiseCode);
    else
      toast.error(
        selectedDesig?.userType === '1'
          ? 'Invalid udise code'
          : 'Invalid teacher id'
      );
  };

  // Angular: submitMultiLogin(2) — Back To User
  const handleBackToUser = () => {
    const backUser = localStorage.getItem('backUser');
    if (backUser) handleMultiUserLogin(2, backUser);
  };

  const backUser = localStorage.getItem('backUser');

  // ── Derived display flags ─────────────────────────────────────────────────
  const showDistrict =
    selectedDesig?.geoType === 'district' || selectedDesig?.geoType === 'block';

  // Block: only when API confirmed blocks exist for selected district
  const showBlock =
    selectedDesig?.geoType === 'block' && blocksLoaded && blockList.length > 0;

  // User ID:
  //   state/cluster        → after designation selected
  //   district             → after district selected
  //   block, no blocks     → immediately after blocksLoaded (skip block step)
  //   block, blocks exist  → only after block selected
  const showUserId =
    selectedDesig?.geoType === 'state' ||
    selectedDesig?.geoType === 'cluster' ||
    (selectedDesig?.geoType === 'district' && !!district) ||
    (selectedDesig?.geoType === 'block' &&
      blocksLoaded &&
      (blockList.length === 0 || !!block));

  const SSO_SECRET_KEY = '83472956108437295012638475920384';
  const SSO_INITIAL_VECTOR = '5061839475201384';

  const encryptSsoUsername = (value: string): string => {
    const key = CryptoJS.enc.Utf8.parse(SSO_SECRET_KEY);
    const iv = CryptoJS.enc.Utf8.parse(SSO_INITIAL_VECTOR);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(value), key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString(); // raw string — matches Angular's URL-param usage
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      header="Multiple Login"
      visible={visible}
      onHide={onHide}
      modal
      blockScroll
      draggable={false}
      appendTo={document.body}
      className="multi-login-dialog"
      style={{ width: '60vw' }}
    >
      <div className="ml-row mt-3">
        {/* Designation — always shown */}
        <div className="ml-col">
          <MandatoryLabel>Designation</MandatoryLabel>
          <Dropdown
            options={designationList}
            value={designation}
            onChange={(e) => handleDesignationChange(e.value)}
            placeholder="Select a designation"
            filter
            appendTo={document.body}
            className="w-100"
          />
        </div>

        {/* District — shown for district and block geoType */}
        {showDistrict && (
          <div className="ml-col">
            <MandatoryLabel>District</MandatoryLabel>
            <Dropdown
              options={districtList}
              value={district}
              onChange={(e) => handleDistrictChange(e.value)}
              placeholder="Select a district"
              filter
              appendTo={document.body}
              className="w-100"
            />
          </div>
        )}

        {/* Block — only when API confirmed blocks exist for this district */}
        {showBlock && (
          <div className="ml-col">
            <MandatoryLabel>Block</MandatoryLabel>
            <Dropdown
              options={blockList}
              value={block}
              onChange={(e) => handleBlockChange(e.value)}
              placeholder="Select a block"
              filter
              appendTo={document.body}
              className="w-100"
            />
          </div>
        )}

        {/* User ID */}
        {showUserId && (
          <div className="ml-col">
            <MandatoryLabel>User ID</MandatoryLabel>
            <Dropdown
              options={multiUserList}
              value={user}
              onChange={(e) => setUser(e.value)}
              placeholder="Select a user"
              filter
              appendTo={document.body}
              className="w-100"
            />
          </div>
        )}

        {/* School geoType — udise code (11) or emis id (8) */}
        {/* {selectedDesig?.geoType === 'school' && (
          <div className="ml-col">
            <MandatoryLabel>
              {selectedDesig.userType === '1' ? 'Udise Code' : 'Emis ID'}
            </MandatoryLabel>
            <InputText
              value={udiseCode}
              onChange={(e) => setUdiseCode(e.target.value)}
              placeholder={
                selectedDesig.userType === '1' ? 'Udisecode' : 'Emis id'
              }
              maxLength={selectedDesig.userType === '1' ? 11 : 8}
              className="w-100"
            />
          </div>
        )} */}
        {selectedDesig?.geoType === 'school' && (
          <div className="ml-col">
            <MandatoryLabel>
              {selectedDesig.userType === '1' ? 'Udise Code' : 'Emis ID'}
            </MandatoryLabel>
            <InputText
              value={udiseCode}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                setUdiseCode(numericValue);
              }}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder={
                selectedDesig.userType === '1' ? 'Udisecode' : 'Emis id'
              }
              maxLength={selectedDesig.userType === '1' ? 11 : 8}
              keyfilter="int"
              inputMode="numeric"
              className="w-100"
            />
          </div>
        )}
      </div>

      <div className="ml-footer">
        <Button
          label="Login"
          className="p-button-success"
          onClick={handleSubmit}
        />
        {backUser && (
          <Button
            label="Back To User"
            className="p-button-secondary"
            onClick={handleBackToUser}
          />
        )}
        <Button
          label="Cancel"
          className="p-button-danger"
          onClick={() => onHide()}
        />
      </div>
    </Dialog>
  );
};

export default MultiLoginModal;
